import React, { useCallback, useId, useState } from 'react';
import { DatePicker } from 'antd';
import moment from 'moment';
import styled, { createGlobalStyle, css } from 'styled-components';

import { formatSupportYearMonth } from '../../../supportFormat';
import { ResponseFormField, ResponseRowLayout } from '../../../types';
import { ResponseIcon } from '../../ResponseActionButton';
import { ResponseRow, ResponseRows } from '../../responsePrimitives';
import { responseType, rsColor, rsRadius, rsv } from '../../responseScale';

export type ResponseFormValues = Record<string, string | null>;

const MONTH_VALUE = 'YYYY-MM';

export const isFieldEditable = (field: ResponseFormField): boolean =>
  field.editable ?? (field.value === null && field.required === true);

export const fieldLayout = (field: ResponseFormField): ResponseRowLayout =>
  field.layout ??
  (isFieldEditable(field) && (field.kind === 'textarea' || field.kind === 'month')
    ? 'stacked'
    : 'inline');

/** The value as the reader sees it: a select's option label, a month as `Oct 2026`. */
export const fieldDisplayValue = (field: ResponseFormField, value: string | null): string => {
  if (value === null) {
    return '';
  }
  if (field.kind === 'select') {
    return field.options?.find(option => option.value === value)?.label ?? value;
  }
  return field.kind === 'month' ? formatSupportYearMonth(value) : value;
};

/** Each field's current value by `key`, seeded from the payload; the form emits it on an action. */
export const useResponseFormValues = (
  fields: ResponseFormField[]
): [ResponseFormValues, (key: string, value: string | null) => void] => {
  const [values, setValues] = useState<ResponseFormValues>(() =>
    Object.fromEntries(fields.map(field => [field.key, field.value]))
  );
  const setValue = useCallback(
    (key: string, value: string | null) => setValues(current => ({ ...current, [key]: value })),
    []
  );
  return [values, setValue];
};

// 6422:10650: the label beside a 600-wide box, both centred on the row.
const BoxedRow = styled.div`
  display: flex;
  align-items: center;
  padding: ${rsv.space('boxedRow')};
  border-bottom: 1px solid ${rsColor.border};

  > dt {
    flex: 0 0 ${rsv.size('fieldLabelColumn')}px;
    color: ${rsv.color('label')};
    ${responseType('body')}
  }

  > dd {
    flex: 1 1 auto;
    min-width: 0;
    margin: 0 0 0 ${rsv.space('boxedLabelGap')}px;
    padding: ${rsv.space('boxedField')};
    border: 1px solid ${rsColor.border};
    border-radius: ${rsv.radius('box')}px;
    background: ${rsv.color('boxed')};
    color: ${rsColor.secondary};
    white-space: pre-line;
    ${responseType('boxed')}
  }
`;

// 6265:4593: label 16 below the row above, the control 8 below the label.
const StackedField = styled.div`
  padding-top: ${rsv.space('fieldLabelTop')}px;

  > dt {
    margin-bottom: ${rsv.space('fieldLabelGap')}px;
    color: ${rsv.color('label')};
    ${responseType('fieldLabel')}
  }

  > dd {
    margin: 0;
  }
`;

const InlineField = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  min-height: ${rsv.size('row')}px;
  padding: ${rsv.space('row')};
  padding-right: ${rsv.space('rowInset')}px;
  padding-left: ${rsv.space('rowInset')}px;
  border-bottom: 1px solid ${rsColor.border};
  ${responseType('body')}

  > dt {
    flex: 0 0 auto;
    color: ${rsv.color('label')};
  }

  > dd {
    min-width: 0;
    margin: 0 0 0 ${rsv.space('rowGap')}px;
  }
`;

const controlBox = css`
  box-sizing: border-box;
  width: 100%;
  border: 1px solid ${rsv.color('fieldRule')};
  border-radius: ${rsv.radius('field')}px;
  background: ${rsColor.inset};
  color: ${rsColor.text};
  font-family: inherit;

  &:focus-visible,
  &:focus-within {
    outline: 2px solid ${rsColor.link};
    outline-offset: 2px;
  }
`;

// 6265:4596: 44 tall, a 16 glyph 10 left of the value.
const FieldBox = styled.div`
  ${controlBox}
  display: flex;
  align-items: center;
  height: ${rsv.size('field')}px;
  padding: ${rsv.space('field')};
  ${responseType('fieldValue')}

  > [aria-hidden] {
    display: inline-flex;
    margin-right: ${rsv.space('headIconGap')}px;
    color: ${rsColor.text};
    font-size: ${rsv.size('fieldIcon')}px;
  }

  > input,
  > select {
    flex: 1 1 auto;
    min-width: 0;
    padding: 0;
    border: none;
    outline: none;
    background: transparent;
    color: inherit;
    font: inherit;
  }

  option {
    background: ${rsColor.panel};
    color: ${rsColor.text};
  }
`;

const MonthPicker = styled(DatePicker)`
  &.ant-picker {
    flex: 1 1 auto;
    min-width: 0;
    padding: 0;
    border: none;
    background: transparent;
    box-shadow: none;
  }

  .ant-picker-input > input,
  .ant-picker-input > input[disabled] {
    color: ${rsColor.text};
    cursor: inherit;
    ${responseType('fieldValue')}
  }
`;

const MONTH_POPUP = 'orbit-response-month-popup';

// The month panel portals to <body>, out of reach of the field's own styles, so it is scoped by
// its class. The design draws no open picker; this keeps it on the page's dark surfaces.
const MonthPopupStyle = createGlobalStyle`
  .${MONTH_POPUP} {
    .ant-picker-panel-container {
      border: 1px solid ${rsColor.border};
      border-radius: ${rsRadius.field}px;
      background: ${rsColor.panel};
    }

    .ant-picker-panel,
    .ant-picker-header {
      border-color: ${rsColor.border};
    }

    .ant-picker-header,
    .ant-picker-header button,
    .ant-picker-cell-in-view {
      color: ${rsColor.text};
    }

    .ant-picker-header button:hover,
    .ant-picker-header-view button:hover {
      color: ${rsColor.link};
    }

    .ant-picker-cell:hover:not(.ant-picker-cell-selected) .ant-picker-cell-inner {
      background: ${rsColor.inset};
    }

    .ant-picker-cell-in-view.ant-picker-cell-selected .ant-picker-cell-inner {
      background: ${rsColor.primary};
      color: ${rsColor.white};
    }

    .ant-picker-cell-in-view.ant-picker-cell-today .ant-picker-cell-inner::before {
      border-color: ${rsColor.link};
    }
  }
`;

// 6265:4602: entered text, not a placeholder.
const TextArea = styled.textarea`
  ${controlBox}
  display: block;
  min-height: ${rsv.size('textarea')}px;
  padding: ${rsv.space('textarea')};
  resize: vertical;
  ${responseType('textarea')}
`;

const InlineControl = styled.select`
  max-width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  color: ${rsColor.text};
  font: inherit;
  font-weight: 700;
  text-align: right;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }

  option {
    background: ${rsColor.panel};
    color: ${rsColor.text};
  }
`;

const InlineInput = styled.input`
  max-width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  color: ${rsColor.text};
  font: inherit;
  font-weight: 700;
  text-align: right;
`;

interface IFieldControlProps {
  id: string;
  field: ResponseFormField;
  value: string | null;
  locked: boolean;
  onChange: (value: string | null) => void;
}

const SelectOptions: React.FC<{ field: ResponseFormField; value: string | null }> = ({
  field,
  value
}) => (
  <>
    {value === null ? (
      <option value="" disabled>
        {field.placeholder ?? ''}
      </option>
    ) : null}
    {field.options?.map(option => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ))}
  </>
);

const StackedControl: React.FC<IFieldControlProps> = ({ id, field, value, locked, onChange }) => {
  switch (field.kind) {
    case 'textarea':
      return (
        <TextArea
          id={id}
          value={value ?? ''}
          placeholder={field.placeholder ?? undefined}
          readOnly={locked}
          rows={2}
          onChange={event => onChange(event.target.value)}
        />
      );
    case 'month':
      return (
        <FieldBox>
          <MonthPopupStyle />
          <ResponseIcon icon={field.icon} />
          <MonthPicker
            id={id}
            picker="month"
            popupClassName={MONTH_POPUP}
            format="MMM YYYY"
            bordered={false}
            allowClear={false}
            suffixIcon={null}
            inputReadOnly
            disabled={locked}
            placeholder={field.placeholder ?? ''}
            value={value === null ? null : moment(value, MONTH_VALUE)}
            onChange={next => onChange(next ? next.format(MONTH_VALUE) : null)}
          />
        </FieldBox>
      );
    case 'select':
      return (
        <FieldBox>
          <ResponseIcon icon={field.icon} />
          <select
            id={id}
            value={value ?? ''}
            disabled={locked}
            onChange={event => onChange(event.target.value)}
          >
            <SelectOptions field={field} value={value} />
          </select>
        </FieldBox>
      );
    default:
      return (
        <FieldBox>
          <ResponseIcon icon={field.icon} />
          <input
            id={id}
            value={value ?? ''}
            placeholder={field.placeholder ?? undefined}
            readOnly={locked}
            onChange={event => onChange(event.target.value)}
          />
        </FieldBox>
      );
  }
};

const InlineEditControl: React.FC<IFieldControlProps> = ({ id, field, value, locked, onChange }) =>
  field.kind === 'select' ? (
    <InlineControl
      id={id}
      value={value ?? ''}
      disabled={locked}
      onChange={event => onChange(event.target.value)}
    >
      <SelectOptions field={field} value={value} />
    </InlineControl>
  ) : (
    <InlineInput
      id={id}
      value={value ?? ''}
      placeholder={field.placeholder ?? undefined}
      readOnly={locked}
      onChange={event => onChange(event.target.value)}
    />
  );

const FormField: React.FC<IFieldControlProps> = props => {
  const { id, field, value } = props;
  if (field.kind === 'readonly_boxed') {
    return (
      <BoxedRow data-testid="orbit-response-field" data-field-key={field.key}>
        <dt>{field.label}</dt>
        <dd>{value}</dd>
      </BoxedRow>
    );
  }
  const layout = fieldLayout(field);
  if (!isFieldEditable(field)) {
    return (
      <ResponseRow
        row={{
          label: field.label,
          value: fieldDisplayValue(field, value),
          tone: field.tone,
          layout
        }}
      />
    );
  }
  if (layout === 'stacked') {
    return (
      <StackedField data-testid="orbit-response-field" data-field-key={field.key}>
        <dt>
          <label htmlFor={id}>{field.label}</label>
        </dt>
        <dd>
          <StackedControl {...props} />
        </dd>
      </StackedField>
    );
  }
  return (
    <InlineField data-testid="orbit-response-field" data-field-key={field.key}>
      <dt>
        <label htmlFor={id}>{field.label}</label>
      </dt>
      <dd>
        <InlineEditControl {...props} />
      </dd>
    </InlineField>
  );
};

export interface IResponseFormFieldsProps {
  fields: ResponseFormField[];
  values: ResponseFormValues;
  /** Fields stay drawn but take no input once the form leaves `draft`. */
  locked: boolean;
  onChange: (key: string, value: string | null) => void;
}

/** 6422:10265 / 6423:10690: every field the payload sends, in its order, nothing more. */
export const ResponseFormFields: React.FC<IResponseFormFieldsProps> = ({
  fields,
  values,
  locked,
  onChange
}) => {
  const formId = useId();
  return (
    <ResponseRows>
      {fields.map(field => (
        <FormField
          key={field.key}
          id={`${formId}-${field.key}`}
          field={field}
          value={values[field.key] ?? null}
          locked={locked}
          onChange={value => onChange(field.key, value)}
        />
      ))}
    </ResponseRows>
  );
};
